<template>
  <div class="order-list-page">
    <div class="page-card">
      <div class="section-header">
        <div class="section-header-left">
          <div class="section-title">{{ activeSectionTitle }}</div>
          <span class="section-sub">共 {{ orderTotal }} 条{{ activeSectionSub }}</span>
        </div>
        <button class="add-btn" type="button" @click="handleCreateOrder">
          + 新建预约
        </button>
      </div>

      <table class="order-table">
        <thead>
          <tr>
            <th class="col-order-id">订单号</th>
            <th>服务项目</th>
            <th>技师</th>
            <th>门诊</th>
            <th>医生</th>
            <th v-if="isReturnList" class="col-reason">退单原因</th>
            <th v-else class="col-appointment">预约信息</th>
            <th class="col-time">{{ isReturnList ? "提交时间" : "预约时间" }}</th>
            <th class="col-action">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="listLoading">
            <td colspan="8" class="empty-cell">加载中...</td>
          </tr>
          <tr v-else-if="!rows.length">
            <td colspan="8" class="empty-cell">暂无待办</td>
          </tr>
          <tr v-for="item in rows" :key="item.orderId || item.id">
            <td class="cell-order-id">{{ item.orderNo || item.id }}</td>
            <td>
              <span class="service-tag service-tag--blue">
                {{ item.service }}
              </span>
            </td>
            <td class="cell-tech">{{ item.tech }}</td>
            <td>
              <span class="clinic-tag">{{ item.clinic }}</span>
            </td>
            <td class="cell-doctor">{{ item.doctorName || "-" }}</td>
            <td v-if="isReturnList" class="cell-reason">
              <div class="reason-title" :class="item.reasonClass">
                {{ item.reasonTitle }}
              </div>
              <div class="reason-desc">{{ item.reasonDesc }}</div>
            </td>
            <td v-else class="cell-appointment">
              <div class="appointment-patient">{{ item.patientName || "-" }}</div>
              <div class="appointment-desc">{{ item.appointmentText || "-" }}</div>
            </td>
            <td class="cell-time">{{ item.time }}</td>
            <td class="col-action">
              <div class="action-btns">
                <button
                  v-if="isReturnList"
                  class="action-btn action-btn-confirm"
                  type="button"
                  @click="handleConfirmReturn(item)"
                >
                  确认退单
                </button>
                <button
                  v-if="showConfirmAction"
                  class="action-btn action-btn-confirm"
                  type="button"
                  @click="handleConfirmOrder(item)"
                >
                  确认预约
                </button>
                <button
                  v-if="isProgressList"
                  class="action-btn action-btn-view"
                  type="button"
                  @click="handleViewDetail(item)"
                >
                  查看详情
                </button>
                <button
                  v-if="isProgressList"
                  class="action-btn action-btn-return"
                  type="button"
                  @click="handleApplyReturn(item)"
                >
                  申请退单
                </button>
                <button
                  v-if="showConfirmAction"
                  class="action-btn action-btn-edit"
                  type="button"
                  @click="handleEdit(item)"
                >
                  修改
                </button>
                <button
                  v-if="isReturnList"
                  class="action-btn action-btn-edit"
                  type="button"
                  @click="handleEdit(item)"
                >
                  重新预约
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="page-card stats-card">
      <div class="section-title stats-title">待办统计</div>
      <div class="stats-grid">
        <div
          v-for="stat in stats"
          :key="stat.key"
          class="stat-item"
          :class="[stat.cardClass, { 'is-active': activeStatus === stat.status }]"
          role="button"
          tabindex="0"
          @click="switchTodoStatus(stat.status)"
          @keyup.enter="switchTodoStatus(stat.status)"
        >
          <div class="stat-value" :class="stat.valueClass">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- 新建预约弹框 -->
    <div
      v-if="showCreateModal"
      class="modal-overlay"
      @click.self="closeCreateModal"
    >
      <div class="modal modal-order-create">
        <div class="modal-header">
          <span class="modal-title">新建预约</span>
          <span class="modal-close" @click="closeCreateModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="createOptionsLoading" class="modal-detail-loading">
            加载选项中...
          </div>
          <template v-else>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>门诊代号
                </label>
                <div
                  class="picker-trigger"
                  role="button"
                  tabindex="0"
                  @click="openHospitalPicker"
                  @keyup.enter="openHospitalPicker"
                >
                  <span
                    class="picker-text"
                    :class="{ 'is-placeholder': !selectedHospitalDisplay }"
                  >
                    {{ selectedHospitalDisplay || "请选择" }}
                  </span>
                  <i class="el-icon-search picker-icon"></i>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>医生姓名
                </label>
                <div
                  class="picker-trigger"
                  :class="{ 'is-disabled': !createForm.hospitalId }"
                  role="button"
                  tabindex="0"
                  @click="openDoctorPicker"
                  @keyup.enter="openDoctorPicker"
                >
                  <span
                    class="picker-text"
                    :class="{ 'is-placeholder': !selectedDoctorDisplay }"
                  >
                    {{ selectedDoctorDisplay || "请选择" }}
                  </span>
                  <i class="el-icon-search picker-icon"></i>
                </div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>患者名字
                </label>
                <input
                  v-model.trim="createForm.patientName"
                  class="form-input"
                  placeholder="请输入患者姓名"
                />
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>修复牙位
                </label>
                <input
                  v-model.trim="createForm.toothPosition"
                  class="form-input"
                  placeholder="如：16, 17, 26, 27"
                />
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>服务项目
                </label>
                <select
                  v-model="createForm.projectId"
                  class="form-select"
                  @change="onServiceProjectChange"
                >
                  <option value="" disabled>请选择服务项目</option>
                  <option
                    v-for="item in serviceOptions"
                    :key="item.projectId"
                    :value="item.projectId"
                  >
                    {{ item.label }} (耗时：{{item.duration}} 小时)
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>修复产品
                </label>
                <select
                  v-model="createForm.repairId"
                  class="form-select"
                  :class="{ 'is-field-disabled': isRepairSelectDisabled }"
                  :disabled="isRepairSelectDisabled"
                >
                  <option value="" disabled>
                    {{
                      projectRepairLoading
                        ? "加载关联修复产品中..."
                        : !createForm.projectId
                          ? "请先选择服务项目"
                          : repairOptions.length
                            ? "请选择修复产品"
                            : "暂无关联修复产品"
                    }}
                  </option>
                  <option
                    v-for="item in repairOptions"
                    :key="item.repairId"
                    :value="item.repairId"
                  >
                    {{ item.repairName }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>预约日期
                </label>
                <el-date-picker
                  v-model="createForm.appointmentDate"
                  class="form-date-picker"
                  type="date"
                  placeholder="选择日期"
                  value-format="yyyy/MM/dd"
                  format="yyyy/MM/dd"
                  :picker-options="appointmentDatePickerOptions"
                  @change="onAppointmentDateChange"
                />
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>预约时间
                </label>
                <select
                  v-model="createForm.timeSlot"
                  class="form-select"
                  :class="{ 'is-field-disabled': isTimeSlotDisabled }"
                  :disabled="isTimeSlotDisabled"
                  @change="syncTechnicianSelectState"
                >
                  <option value="" disabled>
                    {{ isTimeSlotDisabled ? "请先选择预约日期" : "请选择预约时间" }}
                  </option>
                  <option
                    v-for="item in timeSlotOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">指派技师</label>
                <select
                  v-model="createForm.chairId"
                  class="form-select"
                  :class="{ 'is-field-disabled': isTechnicianSelectDisabled }"
                  :disabled="isTechnicianSelectDisabled"
                >
                  <option value="">
                    {{
                      chairListLoading
                        ? "加载可用技师中..."
                        : canSelectTechnician
                          ? "-- 自动匹配 --"
                          : !createForm.timeSlot
                            ? "请先选择预约时间"
                            : "请先完成前置选择"
                    }}
                  </option>
                  <option
                    v-for="item in technicianOptions"
                    :key="item.chairId"
                    :value="item.chairId"
                  >
                    {{ item.label }}({{item.workerId}})
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">数据发送邮箱</label>
                <input
                  v-model.trim="createForm.email"
                  class="form-input"
                  type="email"
                  placeholder="example@clinic.com"
                />
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">备注</label>
              <textarea
                v-model.trim="createForm.remark"
                class="form-textarea"
                rows="3"
                placeholder="患者情况说明、特殊需求等..."
              ></textarea>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeCreateModal"
          >
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingCreate || createOptionsLoading"
            @click="submitCreateOrder"
          >
            {{ savingCreate ? "提交中..." : "创建预约" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 订单详情弹框 -->
    <div
      v-if="showDetailModal"
      class="modal-overlay"
      @click.self="closeDetailModal"
    >
      <div class="modal modal-order-create">
        <div class="modal-header">
          <span class="modal-title">订单详情</span>
          <span class="modal-close" @click="closeDetailModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="detailLoading" class="modal-detail-loading">
            加载详情中...
          </div>
          <template v-else>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">门诊代号</label>
                <div class="detail-text">{{ detailDisplay.hospitalDisplay }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">医生姓名</label>
                <div class="detail-text">{{ detailDisplay.doctorDisplay }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">患者名字</label>
                <div class="detail-text">{{ detailDisplay.patientName }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">修复牙位</label>
                <div class="detail-text">{{ detailDisplay.toothPosition }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">服务项目</label>
                <div class="detail-text">{{ detailDisplay.serviceDisplay }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">修复产品</label>
                <div class="detail-text">{{ detailDisplay.repairName }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">预约日期</label>
                <div class="detail-text">{{ detailDisplay.appointmentDate }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">预约时间</label>
                <div class="detail-text">{{ detailDisplay.timeSlotDisplay }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">指派技师</label>
                <div class="detail-text">{{ detailDisplay.technicianDisplay }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">数据发送邮箱</label>
                <div class="detail-text">{{ detailDisplay.email }}</div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">备注</label>
              <div class="detail-text detail-textarea">{{ detailDisplay.remark }}</div>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeDetailModal"
          >
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 修改订单弹框 -->
    <div
      v-if="showEditModal"
      class="modal-overlay"
      @click.self="closeEditModal"
    >
      <div class="modal modal-order-create">
        <div class="modal-header">
          <span class="modal-title">{{ editModalTitle }}</span>
          <span class="modal-close" @click="closeEditModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="editOptionsLoading" class="modal-detail-loading">
            加载订单信息中...
          </div>
          <template v-else>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">订单编号</label>
                <div class="detail-text">{{ editForm.orderNo || "-" }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">当前指派技师</label>
                <div class="detail-text">{{ editForm.currentChairDisplay || "-" }}</div>
              </div>
            </div>
            <div v-if="!isReturnEditMode" class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">患者名字</label>
                <div class="detail-text">{{ editForm.patientName || "-" }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>修复牙位
                </label>
                <input
                  v-model.trim="editForm.toothPosition"
                  class="form-input"
                  placeholder="如：16, 17, 26, 27"
                />
              </div>
            </div>
            <div v-if="!isReturnEditMode" class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>服务项目
                </label>
                <select
                  v-model="editForm.projectId"
                  class="form-select"
                  @change="onEditServiceProjectChange"
                >
                  <option value="" disabled>请选择服务项目</option>
                  <option
                    v-for="item in serviceOptions"
                    :key="'edit-service-' + item.projectId"
                    :value="item.projectId"
                  >
                    {{ item.label }} (耗时：{{ item.duration }} 小时)
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">
                  <span class="required-mark">*</span>修复产品
                </label>
                <select
                  v-model="editForm.repairId"
                  class="form-select"
                  :class="{ 'is-field-disabled': isEditRepairSelectDisabled }"
                  :disabled="isEditRepairSelectDisabled"
                >
                  <option value="" disabled>
                    {{
                      editProjectRepairLoading
                        ? "加载关联修复产品中..."
                        : !editForm.projectId
                          ? "请先选择服务项目"
                          : editRepairOptions.length
                            ? "请选择修复产品"
                            : "暂无关联修复产品"
                    }}
                  </option>
                  <option
                    v-for="item in editRepairOptions"
                    :key="'edit-repair-' + item.repairId"
                    :value="item.repairId"
                  >
                    {{ item.repairName }}
                  </option>
                </select>
              </div>
            </div>
            <template v-if="isReturnEditMode">
              <div class="form-row form-row-2">
                <div class="form-group">
                  <label class="form-label">
                    <span class="required-mark">*</span>服务项目
                  </label>
                  <select
                    v-model="editForm.projectId"
                    class="form-select"
                    @change="onEditServiceProjectChange"
                  >
                    <option value="" disabled>请选择服务项目</option>
                    <option
                      v-for="item in serviceOptions"
                      :key="'reschedule-service-' + item.projectId"
                      :value="item.projectId"
                    >
                      {{ item.label }} (耗时：{{ item.duration }} 小时)
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">
                    <span class="required-mark">*</span>预约日期
                  </label>
                  <el-date-picker
                    v-model="editForm.appointmentDate"
                    class="form-date-picker"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy/MM/dd"
                    format="yyyy/MM/dd"
                    :picker-options="appointmentDatePickerOptions"
                    @change="onEditAppointmentDateChange"
                  />
                </div>
              </div>
              <div class="form-row form-row-2">
                <div class="form-group">
                  <label class="form-label">
                    <span class="required-mark">*</span>预约时间
                  </label>
                  <select
                    v-model="editForm.timeSlot"
                    class="form-select"
                    :class="{ 'is-field-disabled': isEditTimeSlotDisabled }"
                    :disabled="isEditTimeSlotDisabled"
                    @change="syncEditTechnicianSelectState"
                  >
                    <option value="" disabled>
                      {{ isEditTimeSlotDisabled ? "请先选择预约日期" : "请选择预约时间" }}
                    </option>
                    <option
                      v-for="item in timeSlotOptions"
                      :key="'reschedule-slot-' + item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">指派技师</label>
                  <select
                    v-model="editForm.chairId"
                    class="form-select"
                    :class="{ 'is-field-disabled': isEditTechnicianSelectDisabled }"
                    :disabled="isEditTechnicianSelectDisabled"
                  >
                    <option value="">
                      {{
                        editChairListLoading
                          ? "加载可用技师中..."
                          : canEditSelectTechnician
                            ? "-- 自动匹配 --"
                            : !editForm.timeSlot
                              ? "请先选择预约时间"
                              : "请先完成前置选择"
                      }}
                    </option>
                    <option
                      v-for="item in editTechnicianOptions"
                      :key="'reschedule-chair-' + item.chairId"
                      :value="item.chairId"
                    >
                      {{ item.label }}({{ item.workerId }})
                    </option>
                  </select>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="form-row form-row-2">
                <div class="form-group">
                  <label class="form-label">
                    <span class="required-mark">*</span>预约日期
                  </label>
                  <el-date-picker
                    v-model="editForm.appointmentDate"
                    class="form-date-picker"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy/MM/dd"
                    format="yyyy/MM/dd"
                    :picker-options="appointmentDatePickerOptions"
                    @change="onEditAppointmentDateChange"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">
                    <span class="required-mark">*</span>预约时间
                  </label>
                  <select
                    v-model="editForm.timeSlot"
                    class="form-select"
                    :class="{ 'is-field-disabled': isEditTimeSlotDisabled }"
                    :disabled="isEditTimeSlotDisabled"
                    @change="syncEditTechnicianSelectState"
                  >
                    <option value="" disabled>
                      {{ isEditTimeSlotDisabled ? "请先选择预约日期" : "请选择预约时间" }}
                    </option>
                    <option
                      v-for="item in timeSlotOptions"
                      :key="'edit-slot-' + item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="form-row form-row-2">
                <div class="form-group">
                  <label class="form-label">指派技师</label>
                  <select
                    v-model="editForm.chairId"
                    class="form-select"
                    :class="{ 'is-field-disabled': isEditTechnicianSelectDisabled }"
                    :disabled="isEditTechnicianSelectDisabled"
                  >
                    <option value="">
                      {{
                        editChairListLoading
                          ? "加载可用技师中..."
                          : canEditSelectTechnician
                            ? "-- 自动匹配 --"
                            : !editForm.timeSlot
                              ? "请先选择预约时间"
                              : "请先完成前置选择"
                      }}
                    </option>
                    <option
                      v-for="item in editTechnicianOptions"
                      :key="'edit-chair-' + item.chairId"
                      :value="item.chairId"
                    >
                      {{ item.label }}({{ item.workerId }})
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">数据发送邮箱</label>
                  <input
                    v-model.trim="editForm.email"
                    class="form-input"
                    type="email"
                    placeholder="example@clinic.com"
                  />
                </div>
              </div>
            </template>
            <div v-if="!isReturnEditMode" class="form-group">
              <label class="form-label">备注</label>
              <textarea
                v-model.trim="editForm.remark"
                class="form-textarea"
                rows="3"
                placeholder="患者情况说明、特殊需求等..."
              ></textarea>
            </div>
            <div v-if="isReturnEditMode" class="form-group">
              <label class="form-label">退单原因</label>
              <div class="detail-text">{{ editForm.cancelReason || "-" }}</div>
            </div>
            <div v-if="isReturnEditMode" class="form-group">
              <label class="form-label">退单原因分析</label>
              <div class="detail-text detail-textarea">{{ editForm.cancelReasonDetail || "-" }}</div>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeEditModal"
          >
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingEdit || editOptionsLoading"
            @click="submitEditOrder"
          >
            {{ savingEdit ? "提交中..." : editModalSaveText }}
          </button>
        </div>
      </div>
    </div>

    <!-- 申请退单弹框 -->
    <div
      v-if="showApplyReturnModal"
      class="modal-overlay"
      @click.self="closeApplyReturnModal"
    >
      <div class="modal modal-order-create">
        <div class="modal-header">
          <span class="modal-title">申请退单</span>
          <span class="modal-close" @click="closeApplyReturnModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="applyReturnLoading" class="modal-detail-loading">
            加载订单信息中...
          </div>
          <template v-else>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">订单编号</label>
                <div class="detail-text">{{ applyReturnDisplay.orderNo }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">当前技师</label>
                <div class="detail-text">{{ applyReturnDisplay.currentChairDisplay }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">服务项目</label>
                <div class="detail-text">{{ applyReturnDisplay.serviceDisplay }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">修复产品</label>
                <div class="detail-text">{{ applyReturnDisplay.repairName }}</div>
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-group">
                <label class="form-label">预约日期</label>
                <div class="detail-text">{{ applyReturnDisplay.appointmentDate }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">预约时间</label>
                <div class="detail-text">{{ applyReturnDisplay.timeSlotDisplay }}</div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>退单原因
              </label>
              <input
                v-model.trim="applyReturnForm.cancelReason"
                class="form-input"
                placeholder="如：设备故障、技师请假、客户改期"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                <span class="required-mark">*</span>退单原因详情说明
              </label>
              <textarea
                v-model.trim="applyReturnForm.cancelReasonDetail"
                class="form-textarea"
                rows="3"
                placeholder="请填写退单原因详情说明"
              ></textarea>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeApplyReturnModal"
          >
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingApplyReturn || applyReturnLoading"
            @click="submitApplyReturn"
          >
            {{ savingApplyReturn ? "提交中..." : "提交申请" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 医院选择弹框 -->
    <div
      v-if="showHospitalPicker"
      class="modal-overlay modal-overlay-top"
      @click.self="closeHospitalPicker"
    >
      <div class="modal modal-hospital-picker">
        <div class="modal-header">
          <span class="modal-title">医院选择</span>
          <span class="modal-close" @click="closeHospitalPicker">×</span>
        </div>
        <div class="modal-body hospital-picker-body">
          <div class="hospital-picker-search">
            <div class="hospital-picker-search-item">
              <label class="hospital-picker-search-label">门诊名称</label>
              <input
                v-model.trim="hospitalPickerSearchName"
                class="hospital-picker-search-input"
                placeholder="请输入门诊名称"
                @keyup.enter="handleHospitalPickerSearch"
              />
            </div>
            <div class="hospital-picker-search-item">
              <label class="hospital-picker-search-label">门诊编码</label>
              <input
                v-model.trim="hospitalPickerSearchCode"
                class="hospital-picker-search-input"
                placeholder="请输入门诊编码"
                @keyup.enter="handleHospitalPickerSearch"
              />
            </div>
            <div class="hospital-picker-search-actions">
              <button
                class="search-btn search-btn-reset"
                type="button"
                @click="resetHospitalPickerSearch"
              >
                重置
              </button>
              <button
                class="search-btn search-btn-query"
                type="button"
                :disabled="hospitalPickerLoading"
                @click="handleHospitalPickerSearch"
              >
                查询
              </button>
            </div>
          </div>

          <div
            v-if="hospitalPickerLoading"
            class="hospital-picker-loading"
            :style="{ height: hospitalPickerTableHeight }"
          >
            加载中...
          </div>
          <template v-else>
            <div
              class="hospital-picker-table-wrap"
              :style="{ height: hospitalPickerTableHeight }"
            >
              <table class="hospital-picker-table">
                <thead>
                  <tr>
                    <th class="col-radio">单选</th>
                    <th>门诊名称</th>
                    <th class="col-code">门诊编码</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!hospitalPickerRows.length">
                    <td colspan="3" class="empty-cell">暂无医院</td>
                  </tr>
                  <tr
                    v-for="hospital in hospitalPickerRows"
                    :key="hospital.hospitalId || hospital.hospitalCode"
                    :class="{ 'row-selected': isHospitalPickerDraftSelected(hospital.hospitalId) }"
                    @click="selectHospitalPickerDraft(hospital)"
                  >
                    <td class="col-radio" @click.stop>
                      <input
                        type="radio"
                        name="hospitalPickerRadio"
                        :checked="isHospitalPickerDraftSelected(hospital.hospitalId)"
                        @change="selectHospitalPickerDraft(hospital)"
                      />
                    </td>
                    <td>{{ hospital.hospitalName || "-" }}</td>
                    <td class="col-code">{{ hospital.hospitalCode || "-" }}</td>
                  </tr>
                  <tr
                    v-for="n in emptyHospitalPickerRowCount"
                    :key="'hospital-picker-placeholder-' + n"
                    class="hospital-picker-placeholder-row"
                    aria-hidden="true"
                  >
                    <td class="col-radio">&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <el-pagination
              v-if="hospitalPickerTotal > 0"
              class="hospital-picker-pagination"
              small
              layout="total, prev, pager, next, jumper"
              :current-page="hospitalPickerPageNum"
              :page-size="hospitalPickerPageSize"
              :total="hospitalPickerTotal"
              @current-change="onHospitalPickerPageChange"
            />
          </template>
        </div>
        <div class="modal-footer modal-footer-center">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeHospitalPicker"
          >
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            @click="confirmHospitalPicker"
          >
            确认
          </button>
        </div>
      </div>
    </div>

    <!-- 医生选择弹框 -->
    <div
      v-if="showDoctorPicker"
      class="modal-overlay modal-overlay-top"
      @click.self="closeDoctorPicker"
    >
      <div class="modal modal-doctor-picker">
        <div class="modal-header">
          <span class="modal-title">医生选择</span>
          <span class="modal-close" @click="closeDoctorPicker">×</span>
        </div>
        <div class="modal-body doctor-picker-body">
          <div class="doctor-picker-search">
            <div class="doctor-picker-search-item">
              <label class="doctor-picker-search-label">医生名称</label>
              <input
                v-model.trim="doctorPickerSearchName"
                class="doctor-picker-search-input"
                placeholder="请输入医生名称"
                @keyup.enter="handleDoctorPickerSearch"
              />
            </div>
            <div class="doctor-picker-search-item">
              <label class="doctor-picker-search-label">联系电话</label>
              <input
                v-model.trim="doctorPickerSearchPhone"
                class="doctor-picker-search-input"
                placeholder="请输入联系电话"
                @keyup.enter="handleDoctorPickerSearch"
              />
            </div>
            <div class="doctor-picker-search-actions">
              <button
                class="search-btn search-btn-reset"
                type="button"
                @click="resetDoctorPickerSearch"
              >
                重置
              </button>
              <button
                class="search-btn search-btn-query"
                type="button"
                :disabled="doctorPickerLoading"
                @click="handleDoctorPickerSearch"
              >
                查询
              </button>
            </div>
          </div>

          <div
            v-if="doctorPickerLoading"
            class="doctor-picker-loading"
            :style="{ height: doctorPickerTableHeight }"
          >
            加载中...
          </div>
          <template v-else>
            <div
              class="doctor-picker-table-wrap"
              :style="{ height: doctorPickerTableHeight }"
            >
              <table class="doctor-picker-table">
                <colgroup>
                  <col class="col-radio-col" />
                  <col class="col-equal-col" />
                  <col class="col-equal-col" />
                </colgroup>
                <thead>
                  <tr>
                    <th class="col-radio">单选</th>
                    <th class="col-equal">医生姓名</th>
                    <th class="col-equal">联系方式</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!doctorPickerRows.length">
                    <td colspan="3" class="empty-cell">暂无医生</td>
                  </tr>
                  <tr
                    v-for="doctor in doctorPickerRows"
                    :key="doctor.doctorId"
                    :class="{ 'row-selected': isDoctorPickerDraftSelected(doctor.doctorId) }"
                    @click="selectDoctorPickerDraft(doctor)"
                  >
                    <td class="col-radio" @click.stop>
                      <input
                        type="radio"
                        name="doctorPickerRadio"
                        :checked="isDoctorPickerDraftSelected(doctor.doctorId)"
                        @change="selectDoctorPickerDraft(doctor)"
                      />
                    </td>
                    <td class="col-equal">{{ doctor.realName || "-" }}</td>
                    <td class="col-equal">{{ doctor.phone || "-" }}</td>
                  </tr>
                  <tr
                    v-for="n in emptyDoctorPickerRowCount"
                    :key="'doctor-picker-placeholder-' + n"
                    class="doctor-picker-placeholder-row"
                    aria-hidden="true"
                  >
                    <td class="col-radio">&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <el-pagination
              v-if="doctorPickerTotal > 0"
              class="doctor-picker-pagination"
              small
              layout="total, prev, pager, next, jumper"
              :current-page="doctorPickerPageNum"
              :page-size="doctorPickerPageSize"
              :total="doctorPickerTotal"
              @current-change="onDoctorPickerPageChange"
            />
          </template>
        </div>
        <div class="modal-footer modal-footer-center">
          <button
            class="footer-btn footer-btn-cancel"
            type="button"
            @click="closeDoctorPicker"
          >
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            @click="confirmDoctorPicker"
          >
            确认
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProject, listProjectsWithDevices } from "@/api/device";
import { listDoctors } from "@/api/doctor";
import { listAllHospitals } from "@/api/hospital";
import { applyReturnOrder, confirmOrder, confirmReturnOrder, createOrder, fetchChairList as fetchChairListApi, getOrderDetail, getTodoStats, listOrders, rescheduleOrder, updateOrder } from "@/api/order";
import { listAllRepairs } from "@/api/repair";

const SUCCESS_CODE = 20000;
const HOSPITAL_PICKER_PAGE_SIZE = 10;
const DOCTOR_PICKER_PAGE_SIZE = 10;
const PICKER_ROW_HEIGHT = 44;

const TIME_SLOT_OPTIONS = [
  { value: "am", label: "上午 08:00-12:00" },
  { value: "pm", label: "下午 14:00-18:00" },
  { value: "eve", label: "全天 08:00-18:00" },
];

const ORDER_STATUS = {
  PENDING_CONFIRM: 0,
  IN_PROGRESS: 1,
  RETURN_PENDING: 3,
};

const DEFAULT_ORDER_STATUS = ORDER_STATUS.IN_PROGRESS;

const TODO_STATUS_META = {
  [ORDER_STATUS.RETURN_PENDING]: {
    key: "return",
    label: "退单待处理",
    sectionTitle: "退单待办",
    sectionSub: "待处理",
    cardClass: "stat-item--orange",
    valueClass: "stat-value--orange",
    countField: "returnPendingCount",
  },
  [ORDER_STATUS.PENDING_CONFIRM]: {
    key: "confirm",
    label: "待确认预约",
    sectionTitle: "待确认预约",
    sectionSub: "待确认",
    cardClass: "stat-item--blue",
    valueClass: "stat-value--blue",
    countField: "pendingConfirmCount",
  },
  [ORDER_STATUS.IN_PROGRESS]: {
    key: "progress",
    label: "进行中",
    sectionTitle: "进行中",
    sectionSub: "进行中",
    cardClass: "stat-item--green",
    valueClass: "stat-value--green",
    countField: "inProgressCount",
  },
};

const REASON_CLASS_MAP = {
  设备故障: "reason--orange",
  技师请假: "reason--red",
  客户改期: "reason--blue",
};

function createEmptyForm() {
  return {
    hospitalId: "",
    doctorId: "",
    doctorName: "",
    patientName: "",
    toothPosition: "",
    repairId: "",
    projectId: "",
    appointmentDate: "",
    timeSlot: "",
    chairId: "",
    email: "",
    remark: "",
  };
}

function createEmptyDetailDisplay() {
  return {
    hospitalDisplay: "-",
    doctorDisplay: "-",
    patientName: "-",
    toothPosition: "-",
    serviceDisplay: "-",
    repairName: "-",
    appointmentDate: "-",
    timeSlotDisplay: "-",
    technicianDisplay: "-",
    email: "-",
    remark: "-",
  };
}

function createEmptyEditForm() {
  return {
    orderId: "",
    orderNo: "",
    currentChairDisplay: "",
    hospitalId: "",
    hospitalCode: "",
    hospitalName: "",
    doctorId: "",
    doctorName: "",
    patientName: "",
    toothPosition: "",
    repairId: "",
    repairName: "",
    projectId: "",
    appointmentDate: "",
    timeSlot: "",
    chairId: "",
    email: "",
    remark: "",
    cancelReason: "",
    cancelReasonDetail: "",
  };
}

function createEmptyApplyReturnDisplay() {
  return {
    orderNo: "-",
    currentChairDisplay: "-",
    serviceDisplay: "-",
    repairName: "-",
    appointmentDate: "-",
    timeSlotDisplay: "-",
  };
}

function createEmptyApplyReturnForm() {
  return {
    cancelReason: "",
    cancelReasonDetail: "",
  };
}

export default {
  name: "OrderList",
  data() {
    return {
      showCreateModal: false,
      showDetailModal: false,
      detailLoading: false,
      detailDisplay: createEmptyDetailDisplay(),
      showEditModal: false,
      editOptionsLoading: false,
      savingEdit: false,
      editForm: createEmptyEditForm(),
      editRepairOptions: [],
      editProjectRepairLoading: false,
      editTechnicianOptions: [],
      editChairListLoading: false,
      editModalMode: "edit",
      showApplyReturnModal: false,
      applyReturnLoading: false,
      savingApplyReturn: false,
      applyReturnOrderId: "",
      applyReturnDisplay: createEmptyApplyReturnDisplay(),
      applyReturnForm: createEmptyApplyReturnForm(),
      showHospitalPicker: false,
      createOptionsLoading: false,
      savingCreate: false,
      selectedHospital: null,
      hospitalPickerSearchName: "",
      hospitalPickerSearchCode: "",
      hospitalPickerLoading: false,
      hospitalPickerPageNum: 1,
      hospitalPickerPageSize: HOSPITAL_PICKER_PAGE_SIZE,
      hospitalPickerTotal: 0,
      hospitalPickerRows: [],
      hospitalPickerDraft: null,
      selectedDoctor: null,
      showDoctorPicker: false,
      doctorPickerSearchName: "",
      doctorPickerSearchPhone: "",
      doctorPickerLoading: false,
      doctorPickerPageNum: 1,
      doctorPickerPageSize: DOCTOR_PICKER_PAGE_SIZE,
      doctorPickerTotal: 0,
      doctorPickerAllRows: [],
      doctorPickerRows: [],
      doctorPickerDraft: null,
      doctorPickerHospitalId: "",
      allRepairCatalog: [],
      repairOptions: [],
      projectRepairLoading: false,
      serviceOptions: [],
      technicianOptions: [],
      chairListLoading: false,
      timeSlotOptions: TIME_SLOT_OPTIONS,
      createForm: createEmptyForm(),
      activeStatus: DEFAULT_ORDER_STATUS,
      listLoading: false,
      orderPageNum: 1,
      orderPageSize: 10,
      orderTotal: 0,
      rows: [],
      stats: [
        {
          ...TODO_STATUS_META[ORDER_STATUS.RETURN_PENDING],
          status: ORDER_STATUS.RETURN_PENDING,
          value: 0,
        },
        {
          ...TODO_STATUS_META[ORDER_STATUS.PENDING_CONFIRM],
          status: ORDER_STATUS.PENDING_CONFIRM,
          value: 0,
        },
        {
          ...TODO_STATUS_META[ORDER_STATUS.IN_PROGRESS],
          status: ORDER_STATUS.IN_PROGRESS,
          value: 0,
        },
      ],
    };
  },
  mounted() {
    this.fetchTodoStats();
    this.fetchOrderList();
  },
  computed: {
    activeStatusMeta() {
      return TODO_STATUS_META[this.activeStatus] || TODO_STATUS_META[DEFAULT_ORDER_STATUS];
    },
    activeSectionTitle() {
      return this.activeStatusMeta.sectionTitle;
    },
    activeSectionSub() {
      return this.activeStatusMeta.sectionSub;
    },
    isReturnList() {
      return this.activeStatus === ORDER_STATUS.RETURN_PENDING;
    },
    isProgressList() {
      return this.activeStatus === ORDER_STATUS.IN_PROGRESS;
    },
    showConfirmAction() {
      return this.activeStatus === ORDER_STATUS.PENDING_CONFIRM;
    },
    isReturnEditMode() {
      return this.editModalMode === "reschedule";
    },
    editModalTitle() {
      return this.isReturnEditMode ? "重新预约" : "修改订单";
    },
    editModalSaveText() {
      return this.isReturnEditMode ? "保存预约" : "保存修改";
    },
    appointmentDatePickerOptions() {
      return {
        disabledDate: (date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date.getTime() < today.getTime();
        },
      };
    },
    selectedHospitalDisplay() {
      if (!this.selectedHospital) return "";
      const { hospitalCode, hospitalName } = this.selectedHospital;
      if (hospitalCode && hospitalName) {
        return `${hospitalCode} · ${hospitalName}`;
      }
      return hospitalCode || hospitalName || "";
    },
    hospitalPickerTableHeight() {
      return `${PICKER_ROW_HEIGHT * (HOSPITAL_PICKER_PAGE_SIZE + 1)}px`;
    },
    doctorPickerTableHeight() {
      return `${PICKER_ROW_HEIGHT * (DOCTOR_PICKER_PAGE_SIZE + 1)}px`;
    },
    emptyHospitalPickerRowCount() {
      if (this.hospitalPickerLoading) return 0;
      if (!this.hospitalPickerRows.length) {
        return HOSPITAL_PICKER_PAGE_SIZE - 1;
      }
      return Math.max(
        0,
        this.hospitalPickerPageSize - this.hospitalPickerRows.length
      );
    },
    selectedDoctorDisplay() {
      if (!this.selectedDoctor) return "";
      return this.selectedDoctor.realName+ "("+this.selectedDoctor.phone+")" || "";
    },
    isRepairSelectDisabled() {
      return !this.createForm.projectId || this.projectRepairLoading;
    },
    isTimeSlotDisabled() {
      return !this.createForm.appointmentDate;
    },
    canSelectTechnician() {
      return Boolean(
        this.createForm.hospitalId &&
          this.createForm.projectId &&
          this.createForm.appointmentDate &&
          this.createForm.timeSlot
      );
    },
    isTechnicianSelectDisabled() {
      return !this.canSelectTechnician || this.chairListLoading;
    },
    isEditRepairSelectDisabled() {
      return !this.editForm.projectId || this.editProjectRepairLoading;
    },
    isEditTimeSlotDisabled() {
      return !this.editForm.appointmentDate;
    },
    canEditSelectTechnician() {
      return Boolean(
        this.editForm.hospitalId &&
          this.editForm.projectId &&
          this.editForm.appointmentDate &&
          this.editForm.timeSlot
      );
    },
    isEditTechnicianSelectDisabled() {
      return !this.canEditSelectTechnician || this.editChairListLoading;
    },
    emptyDoctorPickerRowCount() {
      if (this.doctorPickerLoading) return 0;
      if (!this.doctorPickerRows.length) {
        return DOCTOR_PICKER_PAGE_SIZE - 1;
      }
      return Math.max(
        0,
        this.doctorPickerPageSize - this.doctorPickerRows.length
      );
    },
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    resolveListPayload(data) {
      if (Array.isArray(data)) {
        return { list: data, total: data.length };
      }
      if (!data || typeof data !== "object") {
        return { list: [], total: 0 };
      }
      const list = data.list || data.records || data.rows || [];
      const total = Number(data.total ?? list.length) || 0;
      return {
        list: Array.isArray(list) ? list : [],
        total,
      };
    },
    mapHospitalRow(item) {
      if (!item || typeof item !== "object") return null;
      const hospitalId = String(item.hospitalId ?? "").trim();
      if (!hospitalId) return null;
      return {
        hospitalId,
        hospitalCode: String(item.hospitalCode ?? "").trim(),
        hospitalName: String(item.hospitalName ?? "").trim(),
      };
    },
    resolveDoctorListPayload(data) {
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== "object") return [];
      if (Array.isArray(data.list)) return data.list;
      if (Array.isArray(data.records)) return data.records;
      if (Array.isArray(data.rows)) return data.rows;
      return [];
    },
    mapDoctorRow(item) {
      if (!item || typeof item !== "object") return null;
      const doctorId = String(item.doctorId ?? item.id ?? "").trim();
      const realName = String(item.realName ?? item.doctorName ?? "").trim();
      const phone = String(item.phone ?? "").trim();
      if (!doctorId || !realName) return null;
      return { doctorId, realName, phone };
    },
    mapDoctorRows(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => this.mapDoctorRow(item)).filter(Boolean);
    },
    resetDoctorSelection() {
      this.selectedDoctor = null;
      this.createForm.doctorId = "";
      this.createForm.doctorName = "";
      this.doctorPickerAllRows = [];
      this.doctorPickerRows = [];
      this.doctorPickerTotal = 0;
      this.doctorPickerHospitalId = "";
      this.doctorPickerDraft = null;
      this.doctorPickerSearchName = "";
      this.doctorPickerSearchPhone = "";
      this.doctorPickerPageNum = 1;
      this.showDoctorPicker = false;
    },
    openDoctorPicker() {
      if (!this.createForm.hospitalId) {
        this.showMessage("warning", "请先选择门诊代号");
        return;
      }
      this.doctorPickerDraft = this.selectedDoctor
        ? { ...this.selectedDoctor }
        : null;
      this.showDoctorPicker = true;
      this.fetchDoctorPickerList();
    },
    closeDoctorPicker() {
      this.showDoctorPicker = false;
      this.doctorPickerDraft = null;
    },
    isDoctorPickerDraftSelected(doctorId) {
      if (!doctorId || !this.doctorPickerDraft) return false;
      return String(this.doctorPickerDraft.doctorId) === String(doctorId);
    },
    selectDoctorPickerDraft(doctor) {
      if (!doctor || !doctor.doctorId) return;
      this.doctorPickerDraft = {
        doctorId: doctor.doctorId,
        realName: doctor.realName,
        phone: doctor.phone || "",
      };
    },
    confirmDoctorPicker() {
      if (!this.doctorPickerDraft || !this.doctorPickerDraft.doctorId) {
        this.showMessage("warning", "请选择医生");
        return;
      }
      this.selectedDoctor = { ...this.doctorPickerDraft };
      this.createForm.doctorId = this.selectedDoctor.doctorId;
      this.createForm.doctorName = this.selectedDoctor.realName;
      this.closeDoctorPicker();
    },
    resetDoctorPickerSearch() {
      this.doctorPickerSearchName = "";
      this.doctorPickerSearchPhone = "";
      this.doctorPickerPageNum = 1;
      this.applyDoctorPickerFilter();
    },
    handleDoctorPickerSearch() {
      this.doctorPickerPageNum = 1;
      this.applyDoctorPickerFilter();
    },
    onDoctorPickerPageChange(page) {
      this.doctorPickerPageNum = page;
      this.applyDoctorPickerFilter();
    },
    applyDoctorPickerFilter() {
      const nameKeyword = String(this.doctorPickerSearchName || "")
        .trim()
        .toLowerCase();
      const phoneKeyword = String(this.doctorPickerSearchPhone || "").trim();
      let filtered = this.doctorPickerAllRows;
      if (nameKeyword) {
        filtered = filtered.filter((doctor) =>
          String(doctor.realName || "")
            .toLowerCase()
            .includes(nameKeyword)
        );
      }
      if (phoneKeyword) {
        filtered = filtered.filter((doctor) =>
          String(doctor.phone || "").includes(phoneKeyword)
        );
      }
      this.doctorPickerTotal = filtered.length;
      const start = (this.doctorPickerPageNum - 1) * this.doctorPickerPageSize;
      this.doctorPickerRows = filtered.slice(
        start,
        start + this.doctorPickerPageSize
      );
    },
    async fetchDoctorPickerList() {
      const hospitalId = String(this.createForm.hospitalId || "").trim();
      if (!hospitalId) {
        this.doctorPickerAllRows = [];
        this.doctorPickerRows = [];
        this.doctorPickerTotal = 0;
        return;
      }
      const needFetch =
        this.doctorPickerHospitalId !== hospitalId ||
        !this.doctorPickerAllRows.length;
      if (!needFetch) {
        this.applyDoctorPickerFilter();
        return;
      }
      this.doctorPickerLoading = true;
      try {
        const res = await listDoctors({ hospitalId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医生列表加载失败");
          this.doctorPickerAllRows = [];
          this.doctorPickerRows = [];
          this.doctorPickerTotal = 0;
          return;
        }
        const list = this.resolveDoctorListPayload(res && res.data);
        this.doctorPickerAllRows = this.mapDoctorRows(list);
        this.doctorPickerHospitalId = hospitalId;
        this.doctorPickerPageNum = 1;
        this.applyDoctorPickerFilter();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医生列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.doctorPickerAllRows = [];
        this.doctorPickerRows = [];
        this.doctorPickerTotal = 0;
      } finally {
        this.doctorPickerLoading = false;
      }
    },
    mapServiceOptions(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((item) => {
          const projectId = String(item.id ?? item.projectId ?? "").trim();
          const code = String(item.code ?? "").trim();
          const name = String(item.name ?? "").trim();
          if (!projectId) return null;
          const label = code && name ? `${code} ${name}` : code || name || projectId;
          const duration=item.duration
          return { projectId, code, name, label,duration };
        })
        .filter(Boolean);
    },
    mapRepairOptions(list) {
      return (list || [])
        .map((item) => this.normalizeRepairOption(item))
        .filter(Boolean);
    },
    normalizeRepairOption(repair) {
      if (!repair) return null;
      if (typeof repair === "string" || typeof repair === "number") {
        const repairId = String(repair).trim();
        if (!repairId) return null;
        const matched = this.allRepairCatalog.find(
          (item) => String(item.repairId) === repairId
        );
        return matched || { repairId, repairName: repairId };
      }
      const repairId = String(repair.repairId ?? repair.id ?? "").trim();
      const repairName = String(repair.repairName ?? repair.name ?? "").trim();
      if (!repairId) return null;
      return { repairId, repairName: repairName || repairId };
    },
    extractProjectRepairIds(repairPayload) {
      if (!Array.isArray(repairPayload)) return [];
      return repairPayload
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") {
            return String(item).trim();
          }
          return String(item.repairId ?? item.id ?? "").trim();
        })
        .filter(Boolean);
    },
    matchRepairIdsToCatalog(repairIds) {
      const ids = (repairIds || []).map((id) => String(id).trim()).filter(Boolean);
      return ids
        .map((repairId) => {
          const matched = this.allRepairCatalog.find(
            (item) => String(item.repairId) === repairId
          );
          if (matched) return matched;
          const fromPayload = this.normalizeRepairOption({ repairId });
          return fromPayload;
        })
        .filter(Boolean);
    },
    resetProjectRepairOptions() {
      this.createForm.repairId = "";
      this.repairOptions = [];
      this.syncTechnicianSelectState();
    },
    onAppointmentDateChange(value) {
      if (!value) {
        this.createForm.timeSlot = "";
      }
      this.syncTechnicianSelectState();
    },
    syncTechnicianSelectState() {
      if (!this.canSelectTechnician) {
        this.createForm.chairId = "";
        this.technicianOptions = [];
        return;
      }
      this.createForm.chairId = "";
      this.fetchChairList();
    },
    mapAvailableChairOptions(list) {
      return (list || [])
        .map((item) => {
          const chairId = String(item.chairId ?? item.id ?? "").trim();
          const realName = String(
            item.realName ?? item.name ?? item.workerName ?? ""
          ).trim();
          const workerId = String(item.workerId ?? "").trim();
          if (!chairId) return null;
          return {
            chairId,
            realName,
            label: realName || chairId,
            workerId,
          };
        })
        .filter(Boolean);
    },
    async fetchChairList() {
      const { hospitalId, projectId, appointmentDate, timeSlot } = this.createForm;
      if (!hospitalId || !projectId || !appointmentDate || !timeSlot) {
        this.technicianOptions = [];
        return;
      }
      this.chairListLoading = true;
      try {
        const res = await fetchChairListApi({
          hospitalId,
          projectId,
          appointmentDate,
          timeSlot,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "可用技师加载失败");
          this.technicianOptions = [];
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.technicianOptions = this.mapAvailableChairOptions(payload.list);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "可用技师加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.technicianOptions = [];
      } finally {
        this.chairListLoading = false;
      }
    },
    onServiceProjectChange() {
      this.resetProjectRepairOptions();
      if (!this.createForm.projectId) return;
      this.fetchProjectRepairOptions(this.createForm.projectId);
    },
    async fetchProjectRepairOptions(projectId) {
      const id = String(projectId || "").trim();
      if (!id) {
        this.repairOptions = [];
        return;
      }
      this.projectRepairLoading = true;
      try {
        const res = await getProject({ projectId: id });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "服务项目关联修复产品加载失败");
          this.repairOptions = [];
          return;
        }
        const detail = res && res.data != null ? res.data : res;
        const repairIds = this.extractProjectRepairIds(detail.repairIds);
        if (repairIds.length) {
          this.repairOptions = this.matchRepairIdsToCatalog(repairIds);
          return;
        }
        const repairList = this.resolveListPayload(detail).list;
        this.repairOptions = repairList
          .map((item) => this.normalizeRepairOption(item))
          .filter(Boolean);
      } catch (error) {
        const msg =
          (error && error.msg) ||
          error.message ||
          "服务项目关联修复产品加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.repairOptions = [];
      } finally {
        this.projectRepairLoading = false;
      }
    },
    resetCreateForm() {
      this.createForm = createEmptyForm();
      this.selectedHospital = null;
      this.resetDoctorSelection();
      this.repairOptions = [];
      this.projectRepairLoading = false;
      this.technicianOptions = [];
      this.chairListLoading = false;
    },
    openHospitalPicker() {
      this.hospitalPickerDraft = this.selectedHospital
        ? { ...this.selectedHospital }
        : null;
      this.showHospitalPicker = true;
      this.fetchHospitalPickerList();
    },
    closeHospitalPicker() {
      this.showHospitalPicker = false;
      this.hospitalPickerDraft = null;
    },
    isHospitalPickerDraftSelected(hospitalId) {
      if (!hospitalId || !this.hospitalPickerDraft) return false;
      return String(this.hospitalPickerDraft.hospitalId) === String(hospitalId);
    },
    selectHospitalPickerDraft(hospital) {
      if (!hospital || !hospital.hospitalId) return;
      this.hospitalPickerDraft = {
        hospitalId: hospital.hospitalId,
        hospitalCode: hospital.hospitalCode || "",
        hospitalName: hospital.hospitalName || "",
      };
    },
    confirmHospitalPicker() {
      if (!this.hospitalPickerDraft || !this.hospitalPickerDraft.hospitalId) {
        this.showMessage("warning", "请选择医院");
        return;
      }
      const nextHospitalId = this.hospitalPickerDraft.hospitalId;
      const hospitalChanged =
        String(this.createForm.hospitalId) !== String(nextHospitalId);
      this.selectedHospital = { ...this.hospitalPickerDraft };
      this.createForm.hospitalId = this.selectedHospital.hospitalId;
      this.closeHospitalPicker();
      if (hospitalChanged) {
        this.resetDoctorSelection();
        this.syncTechnicianSelectState();
      }
    },
    resetHospitalPickerSearch() {
      this.hospitalPickerSearchName = "";
      this.hospitalPickerSearchCode = "";
      this.hospitalPickerPageNum = 1;
      this.fetchHospitalPickerList();
    },
    handleHospitalPickerSearch() {
      this.hospitalPickerPageNum = 1;
      this.fetchHospitalPickerList();
    },
    onHospitalPickerPageChange(page) {
      this.hospitalPickerPageNum = page;
      this.fetchHospitalPickerList();
    },
    async fetchHospitalPickerList() {
      this.hospitalPickerLoading = true;
      try {
        const res = await listAllHospitals({
          pageNum: this.hospitalPickerPageNum,
          pageSize: this.hospitalPickerPageSize,
          hospitalCode: this.hospitalPickerSearchCode,
          hospitalName: this.hospitalPickerSearchName,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医院列表加载失败");
          this.hospitalPickerRows = [];
          this.hospitalPickerTotal = 0;
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.hospitalPickerRows = (payload.list || [])
          .map((item) => this.mapHospitalRow(item))
          .filter(Boolean);
        this.hospitalPickerTotal = payload.total;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医院列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.hospitalPickerRows = [];
        this.hospitalPickerTotal = 0;
      } finally {
        this.hospitalPickerLoading = false;
      }
    },
    async openCreateModal() {
      this.resetCreateForm();
      this.showCreateModal = true;
      await this.fetchCreateModalOptions();
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.resetCreateForm();
    },
    async fetchOrderFormOptions() {
      const [repairRes, serviceRes] = await Promise.all([
        listAllRepairs(),
        listProjectsWithDevices(),
      ]);

      if (this.isBizError(repairRes)) {
        this.showMessage("error", repairRes.msg || "修复产品加载失败");
        this.allRepairCatalog = [];
      } else {
        this.allRepairCatalog = this.mapRepairOptions(
          this.resolveListPayload(repairRes && repairRes.data).list
        );
      }

      if (this.isBizError(serviceRes)) {
        this.showMessage("error", serviceRes.msg || "服务项目加载失败");
        this.serviceOptions = [];
      } else {
        this.serviceOptions = this.mapServiceOptions(serviceRes && serviceRes.data);
      }
    },
    async fetchCreateModalOptions() {
      this.createOptionsLoading = true;
      try {
        await this.fetchOrderFormOptions();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "选项加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.allRepairCatalog = [];
        this.repairOptions = [];
        this.serviceOptions = [];
      } finally {
        this.createOptionsLoading = false;
      }
    },
    validateCreateForm() {
      const form = this.createForm;
      if (!form.hospitalId) {
        this.showMessage("warning", "请选择门诊代号");
        return false;
      }
      if (!form.doctorId || !form.doctorName) {
        this.showMessage("warning", "请选择医生姓名");
        return false;
      }
      if (!form.patientName) {
        this.showMessage("warning", "请输入患者名字");
        return false;
      }
      if (!form.toothPosition) {
        this.showMessage("warning", "请输入修复牙位");
        return false;
      }
      if (!form.repairId) {
        this.showMessage("warning", "请选择修复产品");
        return false;
      }
      if (!form.projectId) {
        this.showMessage("warning", "请选择服务项目");
        return false;
      }
      if (!form.appointmentDate) {
        this.showMessage("warning", "请选择预约日期");
        return false;
      }
      if (!form.timeSlot) {
        this.showMessage("warning", "请选择预约时间");
        return false;
      }
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        this.showMessage("warning", "请输入正确的邮箱格式");
        return false;
      }
      return true;
    },
    findServiceOption(projectId) {
      const id = String(projectId || "").trim();
      return (this.serviceOptions || []).find(
        (item) => String(item.projectId) === id
      );
    },
    findRepairOption(repairId) {
      const id = String(repairId || "").trim();
      return (this.repairOptions || []).find(
        (item) => String(item.repairId) === id
      );
    },
    findTechnicianOption(chairId) {
      const id = String(chairId || "").trim();
      if (!id) return null;
      return (this.technicianOptions || []).find(
        (item) => String(item.chairId) === id
      );
    },
    buildCreateOrderPayload() {
      const form = this.createForm;
      const hospital = this.selectedHospital || {};
      const doctor = this.selectedDoctor || {};
      const service = this.findServiceOption(form.projectId) || {};
      const repair = this.findRepairOption(form.repairId) || {};
      const chair = this.findTechnicianOption(form.chairId) || {};

      return {
        hospitalId: form.hospitalId,
        hospitalCode: hospital.hospitalCode || "",
        hospitalName: hospital.hospitalName || "",
        doctorId: form.doctorId,
        doctorName: form.doctorName || doctor.realName || "",
        chairId: form.chairId,
        chairName: chair.realName || "",
        workerId: chair.workerId || "",
        patientName: form.patientName,
        projectId: form.projectId,
        projectCode: service.code || "",
        projectName: service.name || "",
        repairName: repair.repairName || "",
        toothPosition: form.toothPosition,
        appointmentDate: form.appointmentDate,
        timeSlot: form.timeSlot,
        email: form.email,
        remark: form.remark,
      };
    },
    async submitCreateOrder() {
      if (!this.validateCreateForm()) return;

      this.savingCreate = true;
      try {
        const res = await createOrder(this.buildCreateOrderPayload());
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "创建预约失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "创建预约成功");
        this.closeCreateModal();
        await Promise.all([this.fetchOrderList(), this.fetchTodoStats()]);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "创建预约失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingCreate = false;
      }
    },
    handleCreateOrder() {
      this.openCreateModal();
    },
    switchTodoStatus(status) {
      const nextStatus = Number(status);
      if (this.activeStatus === nextStatus) return;
      this.activeStatus = nextStatus;
      this.orderPageNum = 1;
      this.fetchOrderList();
    },
    formatTimeSlotLabel(timeSlot) {
      const value = String(timeSlot || "").trim();
      const matched = TIME_SLOT_OPTIONS.find((item) => item.value === value);
      return matched ? matched.label : value || "-";
    },
    displayText(value) {
      const text = String(value ?? "").trim();
      return text || "-";
    },
    resolveCurrentChairDisplay(data) {
      if (!data || typeof data !== "object") return "-";
      const chairName = String(data.chairName ?? "").trim();
      const workerId = String(data.workerId ?? "").trim();
      const chairId = String(data.chairId ?? "").trim();
      if (chairName && workerId) {
        return `${chairName}(${workerId})`;
      }
      if (chairName) return chairName;
      if (workerId) return workerId;
      if (chairId) return chairId;
      return "-";
    },
    resetEditForm() {
      this.editForm = createEmptyEditForm();
      this.editRepairOptions = [];
      this.editProjectRepairLoading = false;
      this.editTechnicianOptions = [];
      this.editChairListLoading = false;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editModalMode = "edit";
      this.resetEditForm();
    },
    findEditRepairOption(repairId) {
      const id = String(repairId || "").trim();
      return (this.editRepairOptions || []).find(
        (item) => String(item.repairId) === id
      );
    },
    findEditTechnicianOption(chairId) {
      const id = String(chairId || "").trim();
      if (!id) return null;
      return (this.editTechnicianOptions || []).find(
        (item) => String(item.chairId) === id
      );
    },
    resolveRepairIdFromDetail(data, repairOptions) {
      const options = repairOptions || [];
      const repairId = String(data.repairId ?? "").trim();
      if (repairId && options.some((item) => String(item.repairId) === repairId)) {
        return repairId;
      }
      const repairName = String(data.repairName ?? "").trim();
      if (!repairName) return repairId;
      const matched = options.find(
        (item) => String(item.repairName) === repairName
      );
      if (matched) return matched.repairId;
      if (repairId) return repairId;
      return repairName;
    },
    resetEditProjectRepairOptions() {
      this.editForm.repairId = "";
      this.editRepairOptions = [];
      this.syncEditTechnicianSelectState();
    },
    onEditAppointmentDateChange(value) {
      if (!value) {
        this.editForm.timeSlot = "";
      }
      this.syncEditTechnicianSelectState();
    },
    syncEditTechnicianSelectState() {
      if (!this.canEditSelectTechnician) {
        this.editForm.chairId = "";
        this.editTechnicianOptions = [];
        return;
      }
      this.editForm.chairId = "";
      this.fetchEditChairList();
    },
    onEditServiceProjectChange() {
      if (this.isReturnEditMode) {
        this.syncEditTechnicianSelectState();
        return;
      }
      this.resetEditProjectRepairOptions();
      if (!this.editForm.projectId) return;
      this.fetchEditProjectRepairOptions(this.editForm.projectId);
    },
    async fetchEditProjectRepairOptions(projectId) {
      const id = String(projectId || "").trim();
      if (!id) {
        this.editRepairOptions = [];
        return;
      }
      this.editProjectRepairLoading = true;
      try {
        const res = await getProject({ projectId: id });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "服务项目关联修复产品加载失败");
          this.editRepairOptions = [];
          return;
        }
        const detail = res && res.data != null ? res.data : res;
        const repairIds = this.extractProjectRepairIds(detail.repairIds);
        if (repairIds.length) {
          this.editRepairOptions = this.matchRepairIdsToCatalog(repairIds);
          return;
        }
        const repairList = this.resolveListPayload(detail).list;
        this.editRepairOptions = repairList
          .map((item) => this.normalizeRepairOption(item))
          .filter(Boolean);
      } catch (error) {
        const msg =
          (error && error.msg) ||
          error.message ||
          "服务项目关联修复产品加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.editRepairOptions = [];
      } finally {
        this.editProjectRepairLoading = false;
      }
    },
    async fetchEditChairList({ preserveChairId = false } = {}) {
      const { hospitalId, projectId, appointmentDate, timeSlot } = this.editForm;
      if (!hospitalId || !projectId || !appointmentDate || !timeSlot) {
        this.editTechnicianOptions = [];
        return;
      }
      const previousChairId = preserveChairId
        ? String(this.editForm.chairId || "").trim()
        : "";
      this.editChairListLoading = true;
      try {
        const res = await fetchChairListApi({
          hospitalId,
          projectId,
          appointmentDate,
          timeSlot,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "可用技师加载失败");
          this.editTechnicianOptions = [];
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.editTechnicianOptions = this.mapAvailableChairOptions(payload.list);
        if (
          previousChairId &&
          this.editTechnicianOptions.some(
            (item) => String(item.chairId) === previousChairId
          )
        ) {
          this.editForm.chairId = previousChairId;
        }
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "可用技师加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.editTechnicianOptions = [];
      } finally {
        this.editChairListLoading = false;
      }
    },
    async applyEditFormFromDetail(data) {
      if (!data || typeof data !== "object") return;

      this.editForm.orderId = String(data.orderId ?? "").trim();
      this.editForm.orderNo = String(
        data.orderNo ?? data.orderCode ?? this.editForm.orderId
      ).trim();
      this.editForm.currentChairDisplay = this.resolveCurrentChairDisplay(data);
      this.editForm.hospitalId = String(data.hospitalId ?? "").trim();
      this.editForm.hospitalCode = String(data.hospitalCode ?? "").trim();
      this.editForm.hospitalName = String(data.hospitalName ?? "").trim();
      this.editForm.doctorId = String(data.doctorId ?? "").trim();
      this.editForm.doctorName = String(data.doctorName ?? "").trim();
      this.editForm.patientName = String(data.patientName ?? "").trim();
      this.editForm.toothPosition = String(data.toothPosition ?? "").trim();
      this.editForm.repairName = String(data.repairName ?? "").trim();
      this.editForm.projectId = String(data.projectId ?? "").trim();
      this.editForm.appointmentDate = String(data.appointmentDate ?? "").trim();
      this.editForm.timeSlot = String(data.timeSlot ?? "").trim();
      this.editForm.chairId = String(data.chairId ?? "").trim();
      this.editForm.email = String(data.email ?? "").trim();
      this.editForm.remark = String(data.remark ?? "").trim();
      this.editForm.cancelReason = String(
        data.cancelReason ?? data.returnReason ?? ""
      ).trim();
      this.editForm.cancelReasonDetail = String(
        data.cancelReasonDetail ?? data.returnReasonDetail ?? ""
      ).trim();

      if (this.editForm.projectId && !this.isReturnEditMode) {
        await this.fetchEditProjectRepairOptions(this.editForm.projectId);
        this.editForm.repairId = this.resolveRepairIdFromDetail(
          data,
          this.editRepairOptions
        );
      } else if (this.isReturnEditMode) {
        this.editForm.repairId = String(data.repairId ?? "").trim();
      }

      if (this.canEditSelectTechnician) {
        await this.fetchEditChairList({ preserveChairId: true });
      }
    },
    async openEditModal(item) {
      const orderId = String(item.orderId ?? item.raw?.orderId ?? "").trim();
      if (!orderId) {
        this.showMessage("warning", "缺少订单ID，无法修改");
        return;
      }

      this.editModalMode = this.isReturnList ? "reschedule" : "edit";
      this.showEditModal = true;
      this.editOptionsLoading = true;
      this.resetEditForm();

      try {
        await this.fetchOrderFormOptions();
        const res = await getOrderDetail({ orderId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "获取订单详情失败");
          this.closeEditModal();
          return;
        }
        await this.applyEditFormFromDetail(res.data);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "加载订单信息失败，请稍后重试";
        this.showMessage("error", msg);
        this.closeEditModal();
      } finally {
        this.editOptionsLoading = false;
      }
    },
    validateEditForm() {
      const form = this.editForm;
      if (!form.orderId) {
        this.showMessage("warning", "缺少订单ID");
        return false;
      }
      if (!this.isReturnEditMode && !form.toothPosition) {
        this.showMessage("warning", "请输入修复牙位");
        return false;
      }
      if (!form.projectId) {
        this.showMessage("warning", "请选择服务项目");
        return false;
      }
      if (!this.isReturnEditMode && !form.repairId) {
        this.showMessage("warning", "请选择修复产品");
        return false;
      }
      if (!form.appointmentDate) {
        this.showMessage("warning", "请选择预约日期");
        return false;
      }
      if (!form.timeSlot) {
        this.showMessage("warning", "请选择预约时间");
        return false;
      }
      if (
        !this.isReturnEditMode &&
        form.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
      ) {
        this.showMessage("warning", "请输入正确的邮箱格式");
        return false;
      }
      return true;
    },
    buildUpdateOrderPayload() {
      const form = this.editForm;
      const service = this.findServiceOption(form.projectId) || {};
      const repair = this.findEditRepairOption(form.repairId) || {};
      const chair = this.findEditTechnicianOption(form.chairId) || {};

      return {
        orderId: form.orderId,
        hospitalId: form.hospitalId,
        hospitalCode: form.hospitalCode || "",
        hospitalName: form.hospitalName || "",
        doctorId: form.doctorId,
        doctorName: form.doctorName || "",
        chairId: form.chairId,
        chairName: chair.realName || "",
        workerId: chair.workerId || "",
        patientName: form.patientName,
        projectId: form.projectId,
        projectCode: service.code || "",
        projectName: service.name || "",
        repairName: repair.repairName || form.repairName || "",
        toothPosition: form.toothPosition,
        appointmentDate: form.appointmentDate,
        timeSlot: form.timeSlot,
        email: form.email,
        remark: form.remark,
      };
    },
    buildRescheduleOrderPayload() {
      const form = this.editForm;
      const service = this.findServiceOption(form.projectId) || {};
      const chair = this.findEditTechnicianOption(form.chairId) || {};

      return {
        orderId: form.orderId,
        chairId: form.chairId,
        chairName: chair.realName || "",
        workerId: chair.workerId || "",
        projectId: form.projectId,
        projectCode: service.code || "",
        projectName: service.name || "",
        appointmentDate: form.appointmentDate,
        timeSlot: form.timeSlot,
      };
    },
    async submitEditOrder() {
      if (!this.validateEditForm()) return;

      this.savingEdit = true;
      try {
        const isReschedule = this.isReturnEditMode;
        const res = isReschedule
          ? await rescheduleOrder(this.buildRescheduleOrderPayload())
          : await updateOrder(this.buildUpdateOrderPayload());
        if (this.isBizError(res)) {
          this.showMessage(
            "error",
            res.msg || (isReschedule ? "重新预约失败" : "修改订单失败")
          );
          return;
        }
        this.showMessage(
          "success",
          (res && res.msg) || (isReschedule ? "重新预约成功" : "修改订单成功")
        );
        this.closeEditModal();
        await Promise.all([this.fetchOrderList(), this.fetchTodoStats()]);
      } catch (error) {
        const isReschedule = this.isReturnEditMode;
        const msg =
          (error && error.msg) ||
          error.message ||
          (isReschedule ? "重新预约失败，请稍后重试" : "修改订单失败，请稍后重试");
        this.showMessage("error", msg);
      } finally {
        this.savingEdit = false;
      }
    },
    mapOrderDetailDisplay(data) {
      if (!data || typeof data !== "object") {
        return createEmptyDetailDisplay();
      }

      const hospitalCode = this.displayText(data.hospitalCode);
      const hospitalName = this.displayText(data.hospitalName);
      const hospitalDisplay =
        hospitalCode !== "-" && hospitalName !== "-"
          ? `${hospitalCode} · ${hospitalName}`
          : hospitalCode !== "-"
            ? hospitalCode
            : hospitalName;

      const doctorName = this.displayText(data.doctorName);
      const doctorPhone = String(data.doctorPhone ?? data.phone ?? "").trim();
      const doctorDisplay =
        doctorName !== "-" && doctorPhone
          ? `${doctorName}(${doctorPhone})`
          : doctorName;

      const serviceDisplay = this.buildServiceDisplay(data);
      const technicianDisplay =
        String(data.chairName ?? "").trim() ||
        String(data.workerId ?? "").trim() ||
        String(data.chairId ?? "").trim()
          ? this.resolveCurrentChairDisplay(data)
          : "自动匹配";

      return {
        hospitalDisplay,
        doctorDisplay,
        patientName: this.displayText(data.patientName),
        toothPosition: this.displayText(data.toothPosition),
        serviceDisplay,
        repairName: this.displayText(data.repairName),
        appointmentDate: this.displayText(data.appointmentDate),
        timeSlotDisplay: this.formatTimeSlotLabel(data.timeSlot),
        technicianDisplay,
        email: this.displayText(data.email),
        remark: this.displayText(data.remark),
      };
    },
    buildServiceDisplay(data) {
      if (!data || typeof data !== "object") return "-";
      const projectCode = this.displayText(data.projectCode);
      const projectName = this.displayText(data.projectName);
      const duration = data.projectDuration ?? data.duration;
      const durationText =
        duration != null && duration !== "" && !Number.isNaN(Number(duration))
          ? ` (耗时：${duration} 小时)`
          : "";
      if (projectCode !== "-" && projectName !== "-") {
        return `${projectCode} ${projectName}${durationText}`;
      }
      if (projectCode !== "-") {
        return `${projectCode}${durationText}`;
      }
      if (projectName !== "-") {
        return `${projectName}${durationText}`;
      }
      return "-";
    },
    mapApplyReturnDisplay(data) {
      if (!data || typeof data !== "object") {
        return createEmptyApplyReturnDisplay();
      }
      const chairDisplay = this.resolveCurrentChairDisplay(data);
      return {
        orderNo: this.displayText(data.orderNo ?? data.orderCode ?? data.orderId),
        currentChairDisplay: chairDisplay === "-" ? "自动匹配" : chairDisplay,
        serviceDisplay: this.buildServiceDisplay(data),
        repairName: this.displayText(data.repairName),
        appointmentDate: this.displayText(data.appointmentDate),
        timeSlotDisplay: this.formatTimeSlotLabel(data.timeSlot),
      };
    },
    resetApplyReturnForm() {
      this.applyReturnOrderId = "";
      this.applyReturnDisplay = createEmptyApplyReturnDisplay();
      this.applyReturnForm = createEmptyApplyReturnForm();
    },
    closeApplyReturnModal() {
      this.showApplyReturnModal = false;
      this.applyReturnLoading = false;
      this.resetApplyReturnForm();
    },
    async handleApplyReturn(item) {
      const orderId = String(item.orderId ?? item.raw?.orderId ?? "").trim();
      if (!orderId) {
        this.showMessage("warning", "缺少订单ID，无法申请退单");
        return;
      }

      this.showApplyReturnModal = true;
      this.applyReturnLoading = true;
      this.resetApplyReturnForm();
      this.applyReturnOrderId = orderId;

      try {
        const res = await getOrderDetail({ orderId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "获取订单详情失败");
          this.closeApplyReturnModal();
          return;
        }
        this.applyReturnDisplay = this.mapApplyReturnDisplay(res.data);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "获取订单详情失败，请稍后重试";
        this.showMessage("error", msg);
        this.closeApplyReturnModal();
      } finally {
        this.applyReturnLoading = false;
      }
    },
    validateApplyReturnForm() {
      if (!this.applyReturnOrderId) {
        this.showMessage("warning", "缺少订单ID");
        return false;
      }
      if (!this.applyReturnForm.cancelReason) {
        this.showMessage("warning", "请填写退单原因");
        return false;
      }
      if (!this.applyReturnForm.cancelReasonDetail) {
        this.showMessage("warning", "请填写退单原因详情说明");
        return false;
      }
      return true;
    },
    async submitApplyReturn() {
      if (!this.validateApplyReturnForm()) return;

      this.savingApplyReturn = true;
      try {
        const res = await applyReturnOrder({
          orderId: this.applyReturnOrderId,
          cancelReason: this.applyReturnForm.cancelReason,
          cancelReasonDetail: this.applyReturnForm.cancelReasonDetail,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "申请退单失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "申请退单成功");
        this.closeApplyReturnModal();
        await Promise.all([this.fetchOrderList(), this.fetchTodoStats()]);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "申请退单失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingApplyReturn = false;
      }
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.detailLoading = false;
      this.detailDisplay = createEmptyDetailDisplay();
    },
    async handleViewDetail(item) {
      const orderId = String(item.orderId ?? item.raw?.orderId ?? "").trim();
      if (!orderId) {
        this.showMessage("warning", "缺少订单ID，无法查看详情");
        return;
      }

      this.showDetailModal = true;
      this.detailLoading = true;
      this.detailDisplay = createEmptyDetailDisplay();

      try {
        const res = await getOrderDetail({ orderId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "获取订单详情失败");
          this.closeDetailModal();
          return;
        }
        this.detailDisplay = this.mapOrderDetailDisplay(res.data);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "获取订单详情失败，请稍后重试";
        this.showMessage("error", msg);
        this.closeDetailModal();
      } finally {
        this.detailLoading = false;
      }
    },
    mapOrderRow(item) {
      if (!item || typeof item !== "object") return null;
      const orderId = String(item.orderId ?? item.id ?? "").trim();
      const orderNo = String(item.orderNo ?? item.orderCode ?? orderId).trim();
      if (!orderId && !orderNo) return null;

      const projectCode = String(item.projectCode ?? "").trim();
      const projectName = String(item.projectName ?? "").trim();
      const service =
        projectCode && projectName
          ? `${projectCode} ${projectName}`
          : projectCode || projectName || "-";

      const chairName = String(item.chairName ?? "").trim();
      const workerId = String(item.workerId ?? "").trim();
      const tech =
        chairName && workerId
          ? `${chairName} (${workerId})`
          : chairName || workerId || "-";

      const hospitalCode = String(item.hospitalCode ?? "").trim();
      const hospitalName = String(item.hospitalName ?? "").trim();
      const clinic = hospitalCode || hospitalName || "-";

      const reasonTitle = String(
        item.cancelReason ?? item.returnReason ?? item.reasonTitle ?? ""
      ).trim();
      const reasonDesc = String(
        item.cancelReasonDetail ?? item.returnReasonDetail ?? item.reasonDesc ?? ""
      ).trim();

      const appointmentDate = String(item.appointmentDate ?? "").trim();
      const timeSlotLabel = this.formatTimeSlotLabel(item.timeSlot);
      const appointmentText =
        appointmentDate && timeSlotLabel
          ? `${appointmentDate} ${timeSlotLabel}`
          : appointmentDate || timeSlotLabel || "";

      const time = String(
        item.submitTime ??
          item.createTime ??
          item.appointmentTime ??
          appointmentText
      ).trim();

      return {
        orderId: orderId || orderNo,
        orderNo: orderNo || orderId,
        service,
        tech,
        clinic,
        doctorName: String(item.doctorName ?? "").trim(),
        patientName: String(item.patientName ?? "").trim(),
        appointmentText,
        reasonTitle: reasonTitle || "-",
        reasonClass: REASON_CLASS_MAP[reasonTitle] || "reason--orange",
        reasonDesc,
        time: time || "-",
        raw: item,
      };
    },
    async fetchTodoStats() {
      try {
        const res = await getTodoStats();
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "获取待办统计失败");
          return;
        }
        const data = (res && res.data) || {};
        this.stats = this.stats.map((stat) => ({
          ...stat,
          value: Number(data[stat.countField] ?? stat.value) || 0,
        }));
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "获取待办统计失败，请稍后重试";
        this.showMessage("error", msg);
      }
    },
    async fetchOrderList() {
      this.listLoading = true;
      try {
        const res = await listOrders({
          status: this.activeStatus,
          pageNum: this.orderPageNum,
          pageSize: this.orderPageSize,
        });
        if (this.isBizError(res)) {
          this.rows = [];
          this.orderTotal = 0;
          this.showMessage("error", res.msg || "获取订单列表失败");
          return;
        }
        const { list, total } = this.resolveListPayload(res.data);
        this.rows = list
          .map((item) => this.mapOrderRow(item))
          .filter(Boolean);
        this.orderTotal = total;
      } catch (error) {
        this.rows = [];
        this.orderTotal = 0;
        const msg =
          (error && error.msg) || error.message || "获取订单列表失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.listLoading = false;
      }
    },
    async handleConfirmReturn(item) {
      const orderId = String(item.orderId ?? item.raw?.orderId ?? "").trim();
      if (!orderId) {
        this.showMessage("warning", "缺少订单ID，无法确认退单");
        return;
      }

      try {
        const res = await confirmReturnOrder({ orderId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "确认退单失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "确认退单成功");
        await Promise.all([this.fetchOrderList(), this.fetchTodoStats()]);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "确认退单失败，请稍后重试";
        this.showMessage("error", msg);
      }
    },
    async handleConfirmOrder(item) {
      const orderId = String(item.orderId ?? item.raw?.orderId ?? "").trim();
      if (!orderId) {
        this.showMessage("warning", "缺少订单ID，无法确认预约");
        return;
      }

      try {
        const res = await confirmOrder({ orderId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "确认预约失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "确认预约成功");
        await Promise.all([this.fetchOrderList(), this.fetchTodoStats()]);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "确认预约失败，请稍后重试";
        this.showMessage("error", msg);
      }
    },
    handleEdit(item) {
      this.openEditModal(item);
    },
  },
};
</script>

<style scoped>
.order-list-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.section-header-left {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.section-sub {
  font-size: 13px;
  color: #999;
}

.add-btn {
  border: none;
  background: #52c41a;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 16px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.add-btn:hover {
  background: #389e0d;
}

.order-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border: 1px solid #eef1f6;
}

.order-table th,
.order-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 14px 12px;
  font-size: 14px;
  color: #3f4a5d;
  vertical-align: middle;
  text-align: left;
}

.order-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.order-table tbody tr:hover td {
  background: #f0f7ff;
}

.order-table .col-order-id {
  width: 130px;
}

.order-table .col-reason {
  width: 180px;
}

.order-table .col-time {
  width: 150px;
  white-space: nowrap;
}

.order-table .col-action {
  width: 280px;
}

.cell-order-id {
  font-weight: 700;
  color: #2f3a4c;
}

.cell-tech,
.cell-doctor,
.cell-time {
  color: #3f4a5d;
}

.service-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  white-space: nowrap;
}

.service-tag--blue {
  color: #1890ff;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
}

.service-tag--green {
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}

.service-tag--purple {
  color: #722ed1;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
}

.clinic-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #722ed1;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
}

.cell-reason,
.cell-appointment {
  line-height: 1.4;
}

.appointment-patient {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
}

.appointment-desc {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
}

.reason-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
}

.reason-desc {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
}

.reason--orange {
  color: #fa8c16;
}

.reason--red {
  color: #ff4d4f;
}

.reason--blue {
  color: #1890ff;
}

.action-btns {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.action-btn-assign {
  border: 1px solid #52c41a;
  background: #52c41a;
  color: #fff;
}

.action-btn-assign:hover {
  background: #389e0d;
  border-color: #389e0d;
}

.action-btn-confirm {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
}

.action-btn-confirm:hover {
  border-color: #409eff;
  color: #409eff;
}

.action-btn-edit {
  border: 1px solid #409eff;
  background: #409eff;
  color: #fff;
}

.action-btn-edit:hover {
  background: #096dd9;
  border-color: #096dd9;
}

.action-btn-view {
  border: 1px solid #409eff;
  background: #fff;
  color: #409eff;
}

.action-btn-view:hover {
  background: #e6f7ff;
  border-color: #096dd9;
  color: #096dd9;
}

.action-btn-return {
  border: 1px solid #fa8c16;
  background: #fff;
  color: #fa8c16;
}

.action-btn-return:hover {
  background: #fff7e6;
  border-color: #d46b08;
  color: #d46b08;
}

.empty-cell {
  text-align: center;
  color: #bfbfbf;
  padding: 32px 0;
}

.stats-card {
  padding-bottom: 24px;
}

.stats-title {
  margin-bottom: 16px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.stat-item {
  border-radius: 10px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.stat-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-item.is-active {
  box-shadow: inset 0 0 0 2px currentColor;
}

.stat-item--orange {
  background: #fff7e6;
}

.stat-item--blue {
  background: #e6f7ff;
}

.stat-item--green {
  background: #f6ffed;
}

.stat-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 8px;
}

.stat-value--orange {
  color: #fa8c16;
}

.stat-value--blue {
  color: #1890ff;
}

.stat-value--green {
  color: #52c41a;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-order-create {
  width: 800px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-detail-loading {
  padding: 32px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row-2 .form-group {
  flex: 1;
  min-width: 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.required-mark {
  color: #ff4d4f;
  margin-right: 4px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  color: #333;
  background: #fff;
}

.form-select {
  height: 36px;
  cursor: pointer;
}

.form-select:disabled,
.form-select.is-field-disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
  box-shadow: none;
}

.form-input:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
  border-color: #d9d9d9;
  box-shadow: none;
}

.detail-text {
  width: 100%;
  min-height: 36px;
  line-height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  color: #333;
  background: #f5f5f5;
  word-break: break-all;
}

.detail-textarea {
  min-height: 80px;
  line-height: 1.5;
  padding: 8px 12px;
  white-space: pre-wrap;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;
  font-family: inherit;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.form-date-picker {
  width: 100%;
}

.form-date-picker >>> .el-input__inner {
  height: 36px;
  line-height: 36px;
  border-radius: 4px;
}

.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.picker-trigger:hover,
.picker-trigger:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.picker-trigger.is-disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.picker-trigger.is-disabled:hover,
.picker-trigger.is-disabled:focus {
  border-color: #d9d9d9;
  box-shadow: none;
}

.picker-text {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-text.is-placeholder {
  color: #bfbfbf;
}

.picker-icon {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 14px;
  color: #bfbfbf;
}

.modal-overlay-top {
  z-index: 1100;
}

.modal-hospital-picker {
  width: 760px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.hospital-picker-body {
  padding: 16px 20px 20px;
}

.hospital-picker-search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 16px;
}

.hospital-picker-search-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hospital-picker-search-label {
  font-size: 14px;
  color: #333;
  line-height: 32px;
  white-space: nowrap;
  flex-shrink: 0;
}

.hospital-picker-search-input {
  width: 180px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.hospital-picker-search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.hospital-picker-search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.search-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.search-btn-reset {
  background: #fff;
  color: #5c6b7f;
  border: 1px solid #d9d9d9;
}

.search-btn-reset:hover {
  color: #409eff;
  border-color: #409eff;
}

.search-btn-query {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.search-btn-query:hover:not(:disabled) {
  background: #096dd9;
  border-color: #096dd9;
}

.search-btn-query:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.hospital-picker-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  box-sizing: border-box;
}

.hospital-picker-table-wrap {
  overflow: hidden;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  box-sizing: border-box;
}

.hospital-picker-table {
  width: 100%;
  border-collapse: collapse;
}

.hospital-picker-table th,
.hospital-picker-table td {
  border-bottom: 1px solid #eef1f6;
  height: 44px;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 22px;
  color: #3f4a5d;
  text-align: left;
  box-sizing: border-box;
  vertical-align: middle;
}

.hospital-picker-table tbody tr:last-child td {
  border-bottom: none;
}

.hospital-picker-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.hospital-picker-table .col-radio {
  width: 72px;
  text-align: center;
}

.hospital-picker-table .col-code {
  width: 140px;
}

.hospital-picker-table .col-radio input[type="radio"] {
  margin: 0;
  vertical-align: middle;
}

.hospital-picker-table tbody tr {
  cursor: pointer;
}

.hospital-picker-table tbody tr:hover td {
  background: #f0f7ff;
}

.hospital-picker-table tbody tr.row-selected td {
  background: #e6f7ff;
}

.hospital-picker-placeholder-row td {
  color: transparent;
  user-select: none;
}

.hospital-picker-pagination {
  margin-top: 16px;
  text-align: right;
}

.modal-doctor-picker {
  width: 660px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.doctor-picker-body {
  padding: 12px 16px 16px;
}

.doctor-picker-search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 16px;
}

.doctor-picker-search-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.doctor-picker-search-label {
  font-size: 14px;
  color: #333;
  line-height: 32px;
  white-space: nowrap;
  flex-shrink: 0;
}

.doctor-picker-search-input {
  width: 140px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.doctor-picker-search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.doctor-picker-search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.doctor-picker-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  box-sizing: border-box;
}

.doctor-picker-table-wrap {
  overflow: hidden;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  box-sizing: border-box;
}

.doctor-picker-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.doctor-picker-table .col-radio-col {
  width: 72px;
}

.doctor-picker-table th,
.doctor-picker-table td {
  border-bottom: 1px solid #eef1f6;
  height: 44px;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 22px;
  color: #3f4a5d;
  text-align: left;
  box-sizing: border-box;
  vertical-align: middle;
}

.doctor-picker-table .col-equal {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doctor-picker-table tbody tr:last-child td {
  border-bottom: none;
}

.doctor-picker-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.doctor-picker-table .col-radio {
  width: 72px;
  text-align: center;
}

.doctor-picker-table .col-radio input[type="radio"] {
  margin: 0;
  vertical-align: middle;
}

.doctor-picker-table tbody tr {
  cursor: pointer;
}

.doctor-picker-table tbody tr:hover td {
  background: #f0f7ff;
}

.doctor-picker-table tbody tr.row-selected td {
  background: #e6f7ff;
}

.doctor-picker-placeholder-row td {
  color: transparent;
  user-select: none;
}

.doctor-picker-pagination {
  margin-top: 12px;
  text-align: right;
}

.modal-footer-center {
  justify-content: center;
}

.modal-footer {
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.footer-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.footer-btn-cancel {
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
}

.footer-btn-cancel:hover {
  color: #409eff;
  border-color: #409eff;
}

.footer-btn-save {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.footer-btn-save:hover {
  background: #096dd9;
  border-color: #096dd9;
}

.footer-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .order-table {
    display: block;
    overflow-x: auto;
  }

  .form-row-2 {
    flex-direction: column;
    gap: 0;
  }
}
</style>
